// NOTIFY-001 SMS Service client (owner, 2026-10-23; D-052): credits and buying a pack (org-wide), and
// per store the order-event messages, the send log and bulk SMS. Shapes: @ady/contracts sms-service.
import { useQuery } from "@tanstack/react-query";
import type {
  SmsAccount,
  SmsBulk,
  SmsBulkEstimate,
  SmsEventRow,
  SmsEvents,
  SmsGroup,
  SmsGroupCounts,
  SmsOrderEvent,
  SmsPayMethod,
  SmsPurchase,
  SmsUsageRow,
} from "@ady/contracts";
import { t } from "@ady/i18n";
import { ApiError, request } from "../api/client";
import { errorMessage } from "../api/errors";

const org = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}/sms`;
const store = (orgId: string, storeId: string) =>
  `/orgs/${encodeURIComponent(orgId)}/stores/${encodeURIComponent(storeId)}/sms`;

export type BulkBody = {
  source: "group" | "orders" | "numbers";
  group?: SmsGroup;
  order_ids?: string[];
  numbers?: string[];
  body: string;
};

export const smsApi = {
  account: (orgId: string) => request<SmsAccount>("GET", `${org(orgId)}/account`),
  buy: (
    orgId: string,
    body: { pack_id: string; method: SmsPayMethod; reference?: string; proof?: string },
    idempotencyKey: string,
  ) => request<SmsPurchase>("POST", `${org(orgId)}/purchases`, { body, idempotencyKey }),
  events: (orgId: string, storeId: string) =>
    request<SmsEvents>("GET", `${store(orgId, storeId)}/events`),
  updateEvent: (
    orgId: string,
    storeId: string,
    event: SmsOrderEvent,
    body: { enabled?: boolean; body?: string; reset?: true },
  ) =>
    request<SmsEventRow>("PUT", `${store(orgId, storeId)}/events/${encodeURIComponent(event)}`, {
      body,
    }),
  usage: (orgId: string, storeId: string) =>
    request<{ data: SmsUsageRow[]; next_before: number | null }>(
      "GET",
      `${store(orgId, storeId)}/usage`,
    ),
  groups: (orgId: string, storeId: string) =>
    request<SmsGroupCounts>("GET", `${store(orgId, storeId)}/bulk/groups`),
  estimate: (orgId: string, storeId: string, body: BulkBody) =>
    request<SmsBulkEstimate>("POST", `${store(orgId, storeId)}/bulk/estimate`, { body }),
  bulks: (orgId: string, storeId: string) =>
    request<{ data: SmsBulk[] }>("GET", `${store(orgId, storeId)}/bulk`),
  sendBulk: (orgId: string, storeId: string, body: BulkBody, idempotencyKey: string) =>
    request<SmsBulk>("POST", `${store(orgId, storeId)}/bulk`, { body, idempotencyKey }),
};

export const smsKeys = {
  account: (orgId: string) => ["org", orgId, "sms", "account"] as const,
  store: (orgId: string, storeId: string) => ["org", orgId, "store", storeId, "sms"] as const,
  events: (orgId: string, storeId: string) => [...smsKeys.store(orgId, storeId), "events"] as const,
  usage: (orgId: string, storeId: string) => [...smsKeys.store(orgId, storeId), "usage"] as const,
  groups: (orgId: string, storeId: string) => [...smsKeys.store(orgId, storeId), "groups"] as const,
  bulks: (orgId: string, storeId: string) => [...smsKeys.store(orgId, storeId), "bulks"] as const,
};

export const useSmsAccount = (orgId: string) =>
  useQuery({ queryKey: smsKeys.account(orgId), queryFn: () => smsApi.account(orgId) });
export const useSmsEvents = (orgId: string, storeId: string) =>
  useQuery({
    queryKey: smsKeys.events(orgId, storeId),
    queryFn: () => smsApi.events(orgId, storeId),
  });
export const useSmsUsage = (orgId: string, storeId: string, enabled = true) =>
  useQuery({
    queryKey: smsKeys.usage(orgId, storeId),
    queryFn: () => smsApi.usage(orgId, storeId),
    enabled,
  });
export const useSmsGroups = (orgId: string, storeId: string, enabled = true) =>
  useQuery({
    queryKey: smsKeys.groups(orgId, storeId),
    queryFn: () => smsApi.groups(orgId, storeId),
    enabled,
  });
export const useSmsBulks = (orgId: string, storeId: string, enabled = true) =>
  useQuery({
    queryKey: smsKeys.bulks(orgId, storeId),
    queryFn: () => smsApi.bulks(orgId, storeId),
    enabled,
  });

/** "order.created" → "created": i18n keys can't hold dots. */
export const smsEventKey = (e: string) =>
  e.replace(/^order./, "") as "created" | "confirmed" | "shipped" | "delivered" | "cancelled";

/** The SMS Service's own reasons first, then the shared error copy. */
export function smsError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "notify.insufficient_credits") return t("smsService.errors.credits");
    if (err.code === "notify.quiet_hours") return t("smsService.errors.quiet");
    if (err.code === "dependency.activation_required") return t("smsService.errors.online");
    if (err.code === "validation.unknown_variable") return t("smsService.errors.variable");
    if (err.fields?.recipients === "none") return t("smsService.errors.nobody");
  }
  return errorMessage(err);
}

/** SMS parts for a text, as the server counts them (GSM-7 160/153, otherwise 70/67). */
const GSM =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXT = "^{}\\[~]|€\f";
export function smsParts(text: string): {
  parts: number;
  chars: number;
  unicode: boolean;
  perPart: number;
} {
  let septets = 0;
  let gsm = true;
  for (const ch of text) {
    if (GSM.includes(ch)) septets += 1;
    else if (GSM_EXT.includes(ch)) septets += 2;
    else {
      gsm = false;
      break;
    }
  }
  if (gsm)
    return {
      parts: septets === 0 ? 0 : septets <= 160 ? 1 : Math.ceil(septets / 153),
      chars: septets,
      unicode: false,
      perPart: septets <= 160 ? 160 : 153,
    };
  const units = text.length;
  return {
    parts: units <= 70 ? 1 : Math.ceil(units / 67),
    chars: units,
    unicode: true,
    perPart: units <= 70 ? 70 : 67,
  };
}

/** A typed number → +977 Nepal mobile, or null (same rule as the server). */
export function nepalMobile(value: string): string | null {
  let v = value
    .normalize("NFC")
    .replace(/[०-९]/g, (d) => String(d.charCodeAt(0) - 0x0966))
    .replace(/[\s().-]/g, "");
  if (v.startsWith("00977")) v = `+${v.slice(2)}`;
  else if (v.startsWith("977")) v = `+${v}`;
  else if (/^0?9\d{9}$/.test(v)) v = `+977${v.replace(/^0/, "")}`;
  return /^\+9779[678]\d{8}$/.test(v) ? v : null;
}

/** Splits pasted text (commas, new lines, spaces between numbers) into number candidates. */
export const splitNumbers = (text: string) =>
  text
    .split(/[\n,;]+|\s{2,}|\t/)
    .map((x) => x.trim())
    .filter(Boolean);
